/** Stage E3.3.a + E3.3.b — Header Regime Chip.
 *
 * Displays live market regime with a 4-state freshness indicator:
 *   Current    (<2h old)          — no badge
 *   Aging      (2-6h old)         — subtle yellow "Aging" badge
 *   Stale      (>6h old)          — red "Stale" badge + explanation in popover
 *   Unavailable (missing/failed)  — muted "Unavailable" badge
 *
 * Popover exposes a Refresh Regime action, the last-updated time, and a11y
 * text warning that sizing may be based on outdated regime data when stale.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Activity, RefreshCw, AlertTriangle, Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";

const REGIME_TONE = {
  STRONG_BULL: "pnl-up", BULL: "pnl-up",
  NEUTRAL: "text-signal-yellow",
  WEAK_BEAR: "pnl-down", STRONG_BEAR: "pnl-down",
  MIXED: "text-muted-foreground",
};

const STALE_STATES = {
  current:     { label: "Current",     tone: "",                             chipClass: "hidden" },
  aging:       { label: "Aging",       tone: "text-signal-yellow",           chipClass: "" },
  stale:       { label: "Stale",       tone: "pnl-down",                     chipClass: "" },
  unavailable: { label: "Unavailable", tone: "text-muted-foreground",        chipClass: "" },
};

function computeFreshness(iso) {
  if (!iso) return "unavailable";
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return "unavailable";
  const ageH = (Date.now() - t) / 3_600_000;
  if (ageH < 0) return "current";
  if (ageH < 2)  return "current";
  if (ageH < 6)  return "aging";
  return "stale";
}

function timeAgo(iso) {
  if (!iso) return "unknown";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff/60_000)}m ago`;
  const h = Math.floor(diff/3_600_000);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h/24)}d ago`;
}

export default function HeaderRegimeChip() {
  const [regime, setRegime] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);  // force re-render so staleness re-computes

  const load = useCallback(async () => {
    try {
      const r = await api.get("/regime/current");
      setRegime(r.data);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const listen = () => load();
    window.addEventListener("tm:regime-refresh", listen);
    // Re-evaluate freshness once per minute so the indicator ticks up.
    const interval = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => {
      window.removeEventListener("tm:regime-refresh", listen);
      clearInterval(interval);
    };
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/regime/current");
      setRegime(r.data);
      setFailed(false);
      window.dispatchEvent(new Event("tm:regime-refresh"));
      toast.success(`Regime refreshed · ${r.data.primary_regime?.replace("_"," ")}`);
    } catch {
      setFailed(true);
      toast.error("Regime refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const freshness = useMemo(() => {
    if (failed || !regime) return "unavailable";
    return computeFreshness(regime.data_timestamp);
  }, [failed, regime, tick]);

  if (!regime && !failed) return null;

  const p = regime?.primary_regime || "MIXED";
  const tone = REGIME_TONE[p] || "";
  const primarySecondary = (regime?.secondary_conditions || [])[0];
  const stale = STALE_STATES[freshness];
  const isStale = freshness === "stale" || freshness === "unavailable";
  const ariaLabel = regime
    ? `Market regime ${p}${primarySecondary ? ", " + primarySecondary : ""}, confidence ${regime.confidence}%, data ${stale.label.toLowerCase()}${isStale ? ", sizing may use outdated regime data" : ""}`
    : "Market regime unavailable — refresh required";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="header-regime-chip"
          data-freshness={freshness}
          aria-label={ariaLabel}
          className={`chip !text-[10px] uppercase tracking-wide border-current hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-current ${tone}`}
        >
          <Activity size={11} className="inline mr-1" aria-hidden="true"/>
          {p.replace("_", " ")}
          {primarySecondary && <span className="mono opacity-80 ml-1">· {primarySecondary.replace(/_/g, " ").toLowerCase()}</span>}
          {regime?.confidence !== undefined && <span className="mono opacity-60 ml-1">{regime.confidence}%</span>}
          {stale.chipClass !== "hidden" && (
            <span
              className={`ml-1.5 px-1 !text-[9px] uppercase tracking-wider border border-current ${stale.tone}`}
              data-testid={`regime-freshness-${freshness}`}
              aria-hidden="true"
            >
              {freshness === "stale" || freshness === "unavailable"
                ? <AlertTriangle size={9} className="inline mr-0.5"/>
                : <Clock size={9} className="inline mr-0.5"/>}
              {stale.label}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" data-testid="header-regime-popover" align="end">
        <div className="p-3 border-b border-border/50 flex items-center gap-2">
          <Activity size={12} aria-hidden="true"/>
          <div className="text-sm mono">Market Regime</div>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="ml-auto chip !text-[10px] border-current text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current"
            data-testid="header-regime-refresh"
            aria-label="Refresh market regime"
          >
            <RefreshCw size={10} className={`inline mr-1 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true"/>Refresh Regime
          </button>
        </div>
        <div className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
          {isStale && regime && (
            <div
              className={`border p-2 text-[11px] flex items-start gap-2 ${
                freshness === "stale" ? "border-signal-red/60 bg-signal-red/5" : "border-border/60 bg-secondary/10"
              }`}
              role="status"
              aria-live="polite"
              data-testid="regime-stale-warning"
            >
              <AlertTriangle size={12} className="pnl-down shrink-0 mt-0.5" aria-hidden="true"/>
              <div>
                <div className="mono uppercase text-[10px] pnl-down">{stale.label} regime data</div>
                <div className="text-muted-foreground mt-0.5">
                  Last updated {timeAgo(regime.data_timestamp)}. Position sizing and strategy
                  guidance may be based on outdated regime information. Refresh before approving trades.
                </div>
              </div>
            </div>
          )}
          {failed && (
            <div className="border border-signal-red/60 bg-signal-red/5 p-2 text-[11px]" role="alert" data-testid="regime-unavailable-warning">
              Regime data is currently unavailable. Retry with the Refresh button above.
            </div>
          )}
          {regime && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`chip !text-[10px] uppercase border-current ${tone}`}>{p.replace("_"," ")}</span>
                {(regime.secondary_conditions || []).map((s, i) => (
                  <span key={i} className="chip !text-[10px] border-current text-muted-foreground">{s.replace(/_/g," ").toLowerCase()}</span>
                ))}
              </div>
              <div className="mono text-[10px] text-muted-foreground">
                Confidence {regime.confidence}% · Sizing multiplier {regime.sizing_multiplier} · Updated {timeAgo(regime.data_timestamp)} · {regime.data_quality}
              </div>
              <div className="border-t border-border/40 pt-2">
                <table className="w-full text-xs">
                  <caption className="sr-only">Regime score breakdown</caption>
                  <tbody>
                    {(regime.score_breakdown || []).map((r, i) => (
                      <tr key={i} className="border-b border-border/20">
                        <td className="mono py-1 text-[10px]">{r.signal.replace(/_/g," ")}</td>
                        <td className="text-[11px] py-1 text-muted-foreground">{r.status}</td>
                        <td className={`mono text-right py-1 ${r.contribution >= 0 ? "pnl-up" : "pnl-down"}`}>{r.contribution >= 0 ? "+" : ""}{r.contribution}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {regime.strategy_guidance && (
                <div className="text-[11px] space-y-0.5">
                  {regime.strategy_guidance.favored?.length > 0 && (
                    <div><span className="mono uppercase text-[9px] pnl-up mr-1">Favoured:</span>{regime.strategy_guidance.favored.join(", ")}</div>
                  )}
                  {regime.strategy_guidance.restricted?.length > 0 && (
                    <div><span className="mono uppercase text-[9px] pnl-down mr-1">Restricted:</span>{regime.strategy_guidance.restricted.join(", ")}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
